import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { filter, map, skip, switchMap, take, timeout } from 'rxjs/operators';
import { PermissionService } from '../../core/services/permission.service';
import { StorageKey, StorageService } from '../../core/services/storage.service';
import { JwtUtils } from '../../core/utils/jwt.utils';
import { PosStateService } from '../pos/pos-state.service';
import { isPaytermUrl, paytermEntryCommands } from './payterm.models';

/**
 * PTM-3 / PTM-4 — Payment Terminal route guards
 * (docs/PAYTERM-PAYMENT-TERMINAL-MODE-PLAN.md §6.1-§6.2).
 *
 * Every denial here lands on the payterm entry (`/pos/s/<code>/pt`), never on
 * a core POS route: the core guards bounce to `/pos/terminal` / `/kds`, which
 * is exactly the "leak into the full POS" the plan exists to prevent.
 */

/** Codes that let a user take a payment on the terminal (§6.3). */
export const PAYTERM_PAYMENT_PERMISSIONS: readonly string[] = ['pos.payment.basic', 'pos.payment.integrated'];

/** Shift-unlock mode (PIN not required, D3): only people trusted to open a shift unlock the terminal. */
export const PAYTERM_SHIFT_UNLOCK_PERMISSION = 'pos.session.open';

/** Query param the entry reads to explain why it was sent there. */
export const PAYTERM_REASON_PARAM = 'reason';

/** Why a guard (or a re-check) sent the operator back to the entry. Shown there, never silently dropped. */
export type PaytermEntryReason =
  | 'no-payment-permission'
  | 'no-pos-access'
  | 'permissions-unavailable'
  | 'no-shift-permission';

const REASON_KEYS: Record<PaytermEntryReason, string> = {
  'no-payment-permission': 'pos.payterm.guard.noPaymentPermission',
  'no-pos-access': 'pos.payterm.guard.noPosAccess',
  'permissions-unavailable': 'pos.payterm.guard.permissionsUnavailable',
  // PTM-3 — D3 re-check: a session kept unlocked for the shift by someone who may not open one.
  'no-shift-permission': 'pos.payterm.guard.noShiftPermission',
};

/** Transloco key for a `?reason=` value, or null for anything unknown. */
export function paytermReasonKey(reason: string | null | undefined): string | null {
  if (!reason) return null;
  return (REASON_KEYS as Record<string, string>)[reason] ?? null;
}

/** How long a guard / the entry waits for PermissionService before calling it a failure. */
export const PAYTERM_PERMISSIONS_WAIT_MS = 10_000;

/** `{ returnUrl }` only when the URL is inside the payterm surface — core URLs are dropped. */
export function paytermReturnUrlParams(url: string | null | undefined): Record<string, string> {
  return url && isPaytermUrl(url) ? { returnUrl: url } : {};
}

function sameCode(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/**
 * The POS session as the payterm surface sees it, for store `code`:
 *   'ok'            — standalone code for THIS store + unexpired POS_TOKEN in storage,
 *                     the in-memory session carrying that same token, canAccessPos === true
 *   'no-pos-access' — signed in, but the backend did not grant POS access
 *   'signed-out'    — anything else
 *
 * Mirrors posGuard + posAuthGuard WITHOUT their redirects and without
 * `posState.isAuthenticated`, whose expiry branch runs clearPosState() (it
 * would wipe POS_STANDALONE_CODE from inside a guard). Storage AND memory are
 * both checked: the 401 interceptor clears the storage keys only, and a
 * memory-only session would bounce entry → tables → entry forever.
 */
export function paytermSessionState(
  storage: StorageService,
  posState: PosStateService,
  code: string,
): 'ok' | 'no-pos-access' | 'signed-out' {
  const standalone = storage.getRaw(StorageKey.POS_STANDALONE_CODE);
  const token = storage.getRaw(StorageKey.POS_TOKEN);
  if (!standalone || !token || !sameCode(standalone, code)) return 'signed-out';
  if (JwtUtils.isExpired(token)) return 'signed-out';
  const auth = posState.posAuth;
  if (!auth || auth.token !== token) return 'signed-out';
  // POS-ACCESS — an explicit true only, same as posAuthGuard; undefined is a
  // pre-flag session we cannot vouch for.
  if (auth.canAccessPos !== true) return 'no-pos-access';
  return 'ok';
}

/**
 * Resolves true once PermissionService has SETTLED for the current store — a
 * fetched set, or (careful) a failed fetch, which PermissionService also marks
 * loaded and answers from the legacy role map. Use {@link ensureFetchedPermissions}
 * / {@link paytermAccessVerdict} before deciding anything on it. False after
 * `timeoutMs`. BehaviorSubject replay: already settled ⇒ immediate.
 */
export function awaitPermissionsLoaded(
  perm: PermissionService,
  timeoutMs = PAYTERM_PERMISSIONS_WAIT_MS,
): Observable<boolean> {
  return perm.permissions$.pipe(
    filter(() => perm.isLoaded),
    take(1),
    map(() => true),
    timeout({ first: timeoutMs, with: () => of(false) }),
  );
}

/**
 * Like {@link awaitPermissionsLoaded} but ignores the set held NOW: resolves on
 * the next settled load. Subscribe BEFORE `posState.setPosSession()` so the
 * answer is the reload that call starts for the user who just signed in —
 * never the previous user's set, which `isLoaded` alone cannot tell apart.
 */
export function awaitFreshPermissions(
  perm: PermissionService,
  timeoutMs = PAYTERM_PERMISSIONS_WAIT_MS,
): Observable<boolean> {
  return perm.permissions$.pipe(
    skip(1),
    filter(() => perm.isLoaded),
    take(1),
    map(() => true),
    timeout({ first: timeoutMs, with: () => of(false) }),
  );
}

/**
 * PTM-3 — the set PermissionService holds was really FETCHED for this user.
 * A failed fetch (5xx, 503, transport error) also settles as "loaded", but
 * PermissionService.markLoadFailed() leaves it with no roles and no codes and
 * answers from the legacy role map — any "Cashier" / "Manager" would pass,
 * whatever the tenant granted. The terminal is online only (D7), so that map
 * never decides here. A real set always carries roles or codes (a TenantAdmin /
 * Owner is floored to the full catalogue server-side); a role-less user's empty
 * set reads as "unavailable" too — refused either way, with a visible reason.
 */
export function paytermPermissionsFetched(perm: PermissionService): boolean {
  return perm.isLoaded && perm.isNewModel;
}

/**
 * PTM-3 — `loaded` (from {@link awaitPermissionsLoaded} / {@link awaitFreshPermissions})
 * turned into "a fetched set is held". A settled-but-failed load gets ONE reload
 * (PermissionService.reload(), subscribed first so its answer is not missed)
 * before it counts as unavailable — a transient 5xx, or the offline→online race
 * with PermissionService's own retry, does not lock anyone out.
 */
export function ensureFetchedPermissions(
  perm: PermissionService,
  loaded: boolean,
  timeoutMs = PAYTERM_PERMISSIONS_WAIT_MS,
): Observable<boolean> {
  if (!loaded) return of(false);
  if (paytermPermissionsFetched(perm)) return of(true);
  return new Observable<boolean>(subscriber => {
    const sub = awaitFreshPermissions(perm, timeoutMs)
      .pipe(map(ok => ok && paytermPermissionsFetched(perm)))
      .subscribe(subscriber);
    perm.reload();
    return sub;
  });
}

/** What the terminal may do for the signed-in user, from a settled permission set. */
export type PaytermAccessVerdict =
  | 'ok'
  | Extract<PaytermEntryReason, 'permissions-unavailable' | 'no-payment-permission' | 'no-shift-permission'>;

/**
 * PTM-3 — the one gate the entry (fresh PIN and an existing session), the shell
 * (shift-unlock re-check) and the settle guard all apply:
 *   no fetched set                         → 'permissions-unavailable'
 *   no pos.payment.basic | .integrated     → 'no-payment-permission'
 *   shift unlock (D3) without pos.session.open → 'no-shift-permission'
 * `fetched` is the result of {@link ensureFetchedPermissions}.
 */
export function paytermAccessVerdict(
  perm: PermissionService,
  fetched: boolean,
  shiftUnlock: boolean,
): PaytermAccessVerdict {
  if (!fetched || !paytermPermissionsFetched(perm)) return 'permissions-unavailable';
  if (!perm.hasAnyPermission(...PAYTERM_PAYMENT_PERMISSIONS)) return 'no-payment-permission';
  if (shiftUnlock && !perm.hasPermission(PAYTERM_SHIFT_UNLOCK_PERMISSION)) return 'no-shift-permission';
  return 'ok';
}

/**
 * PTM-4 — the anti-leak guard, added FIRST to every core `pos*` route in
 * app.routes.ts. On a device marked as a Payment Terminal
 * (StorageKey.PAYTERM_DEVICE), every road back into the full POS — the PIN
 * screen's `/pos/terminal` default, lockScreen(), the 409 session_closed and
 * 401 recoveries, the NotFound CTA, the offline banner's links — ends on the
 * payterm entry instead. A plain browser is never marked, so testers are not
 * trapped. Keeps `returnUrl` only when it is itself a payterm URL.
 */
export const paytermDeviceRedirectGuard: CanActivateFn = (route) => {
  const storage = inject(StorageService);
  const router = inject(Router);
  const marker = (storage.getRaw(StorageKey.PAYTERM_DEVICE) ?? '').trim();
  if (!marker) return true;
  const code = route.paramMap?.get('posStoreCode') || marker;
  return router.createUrlTree(paytermEntryCommands(code), {
    queryParams: paytermReturnUrlParams(route.queryParamMap?.get('returnUrl')),
  });
};

/**
 * PTM-3 / PTM-6 — the payterm shell (tables + settle, at `/pos/pt/…`). The
 * store is POS_STANDALONE_CODE — the working screens carry no code, and only
 * the entry stamps it, after checking the session against ITS URL's store (the
 * cross-store check lives there). Needs an unexpired POS_TOKEN for that store +
 * canAccessPos === true; otherwise → the payterm entry with a payterm-only
 * `returnUrl` (and a reason when access was refused). No stamped code at all (a
 * `/pos/pt` deep link on a device that never saw the entry) → the neutral
 * `/pos`, where the device redirect guard re-routes a marked terminal.
 */
export const paytermAuthGuard: CanActivateFn = (_route, state) => {
  const storage = inject(StorageService);
  const posState = inject(PosStateService);
  const router = inject(Router);
  const code = storage.getRaw(StorageKey.POS_STANDALONE_CODE);
  if (!code) return router.createUrlTree(['/pos']);

  const verdict = paytermSessionState(storage, posState, code);
  if (verdict === 'ok') return true;
  const queryParams: Record<string, string> = paytermReturnUrlParams(state.url);
  if (verdict === 'no-pos-access') queryParams[PAYTERM_REASON_PARAM] = 'no-pos-access';
  return router.createUrlTree(paytermEntryCommands(code), { queryParams });
};

/**
 * PTM-3 — the payterm settle route. Needs `pos.payment.basic` or
 * `pos.payment.integrated` (NOT the view-only `pos.payment.view` that
 * posPaymentGuard accepts: the terminal exists to take money). Waits for
 * PermissionService to settle rather than deciding on the pre-load role map,
 * and never decides on the role map a FAILED fetch leaves behind either (one
 * reload, then 'permissions-unavailable'); a load that never settles is its
 * own, visible reason. Denial → the payterm entry, never `/pos/terminal?denied=`.
 */
export const paytermPaymentGuard: CanActivateFn = () => {
  const perm = inject(PermissionService);
  const storage = inject(StorageService);
  const router = inject(Router);
  // PTM-6 — the settle URL carries no store code; the entry stamped it.
  const code = storage.getRaw(StorageKey.POS_STANDALONE_CODE) ?? '';
  const deny = (reason: PaytermEntryReason) =>
    router.createUrlTree(code ? paytermEntryCommands(code) : ['/pos'], {
      queryParams: { [PAYTERM_REASON_PARAM]: reason },
    });

  return awaitPermissionsLoaded(perm).pipe(
    switchMap(loaded => ensureFetchedPermissions(perm, loaded)),
    map(fetched => {
      // Payment only: the shift-unlock re-check lives in the shell (it has the settings).
      const verdict = paytermAccessVerdict(perm, fetched, false);
      return verdict === 'ok' ? true : deny(verdict);
    }),
  );
};
